import { create } from 'zustand';

import { splitWords } from '../composer/composer';
import { subscribe, type AppEvent } from '../events';
import { personalNgramRepo } from '../../data/repositories';
import { normaliseForMatch } from '../prediction/fold';
import { bigramKey, type PersonalCounts } from '../prediction/PredictionProvider';
import type { UtteranceSource } from '../types';

/**
 * Learning: what this person says becomes what the device expects them to say.
 *
 * Subscribes to `UtteranceSpoken`, exactly like `DiagnosticsService` — and the two are **opposites on
 * the same event**, which is worth saying plainly at both sites so neither is ever "tidied" into the
 * other:
 *
 *   - `DiagnosticsService` must **strip the text** and keep only metadata (NFR7).
 *   - `LearningService` must **keep the text**, because the words are the entire point.
 *
 * Both are correct. The difference is that diagnostics are observations *about* the person, which have
 * no business holding their sentences, while the n-grams *are* the person's own vocabulary — the part of
 * this database that cannot be recreated from anything else, and the reason Phase 4's backup matters
 * from today rather than eventually.
 *
 * Everything here is fire-and-forget. Learning happens after the utterance has been dispatched and must
 * never delay speaking — the same treatment `composer.commit()` already gives history.
 */

/**
 * Which sources teach.
 *
 * **Quick fires never do**, and 2.3 is explicit about it. Six words tapped dozens of times a day would
 * dominate the personal unigrams outright, and `ja` outranking the German language at the letter `j` is
 * not a prediction — it is noise the person has to look past on every word starting with j.
 *
 * **Phrase taps do.** A phrase tapped twenty times is words this person chose to say, and boosting them
 * helps when they later type a variation of the same thought. The cost is honest: a caregiver wrote
 * those words, so the ranking partly reflects someone else's phrasing.
 */
const TEACHING_SOURCES: readonly UtteranceSource[] = ['keyboard', 'phrase', 'prediction'];

export const teaches = (source: UtteranceSource): boolean => TEACHING_SOURCES.includes(source);

/**
 * The counts the prediction worker scores against, held in memory on the main thread.
 *
 * The worker must not touch IndexedDB — it would open a second connection to a database whose
 * durability we are careful about, and put a database read on the < 100 ms path. So the main thread owns
 * storage and pushes a snapshot after each utterance: once per sentence, not per keystroke.
 */
interface LearningState {
  counts: PersonalCounts;
  /** Distinct words heard. Shown in care mode — the only visible sign the feature is alive. */
  wordCount: number;
  hydrate: () => Promise<void>;
  /** Called by the service; exposed for tests. */
  learn: (text: string, source: UtteranceSource) => Promise<void>;
}

interface MutableCounts {
  unigrams: Map<string, number>;
  bigrams: Map<string, number>;
  lastUsed: Map<string, number>;
  display: Map<string, string>;
}

const emptyMaps = (): MutableCounts => ({
  unigrams: new Map(),
  bigrams: new Map(),
  lastUsed: new Map(),
  display: new Map(),
});

export const useLearning = create<LearningState>((set, get) => ({
  counts: emptyMaps(),
  wordCount: 0,

  hydrate: async () => {
    try {
      const [unigrams, bigrams] = await Promise.all([
        personalNgramRepo.allUnigrams(),
        personalNgramRepo.allBigrams(),
      ]);

      // Rows are **merged**, not mapped: two stored spellings of one word are one word here. Storage
      // keeps the raw tokens, because an export is a record of what was actually said; the fold belongs
      // on this side of the boundary, where the scorer will look words up.
      const merged = emptyMaps();
      /** Folded key → the count behind the spelling currently in `display`. */
      const bestSeen = new Map<string, number>();

      for (const row of unigrams) {
        const key = normaliseForMatch(row.word);
        merged.unigrams.set(key, (merged.unigrams.get(key) ?? 0) + row.count);
        merged.lastUsed.set(
          key,
          Math.max(merged.lastUsed.get(key) ?? 0, Date.parse(row.lastUsedAt)),
        );
        // The spelling heard most often is the one worth showing back.
        if (row.count > (bestSeen.get(key) ?? 0)) {
          bestSeen.set(key, row.count);
          merged.display.set(key, row.word);
        }
      }

      for (const row of bigrams) {
        const key = bigramKey(normaliseForMatch(row.prev), normaliseForMatch(row.next));
        merged.bigrams.set(key, (merged.bigrams.get(key) ?? 0) + row.count);
      }

      const counts: PersonalCounts = merged;
      set({ counts, wordCount: merged.unigrams.size });
      notify(counts);
    } catch (err) {
      // Prediction degrades to corpus-only ranking, which is what stage 1 shipped. Not worth
      // interrupting anything for.
      console.error('[learning] could not load personal counts', err);
    }
  },

  learn: async (text, source) => {
    if (!teaches(source)) return;

    const words = splitWords(text);
    if (words.length === 0) return;

    const spokenAt = new Date().toISOString();
    const now = Date.now();

    // Updated in memory first so the next prediction already reflects this sentence, even if the write
    // is still in flight. The person should not have to wait for a transaction to benefit from what they
    // just said.
    const { counts } = get();
    const unigrams = new Map(counts.unigrams);
    const bigrams = new Map(counts.bigrams);
    const lastUsed = new Map(counts.lastUsed);
    const display = new Map(counts.display);

    for (const [index, word] of words.entries()) {
      const key = normaliseForMatch(word);
      unigrams.set(key, (unigrams.get(key) ?? 0) + 1);
      lastUsed.set(key, now);
      // First spelling wins for the rest of the session. The next `hydrate` sees the whole distribution
      // and settles it properly; guessing harder from one sentence would not be better.
      if (!display.has(key)) display.set(key, word);
      const next = words[index + 1];
      if (next !== undefined) {
        const pair = bigramKey(key, normaliseForMatch(next));
        bigrams.set(pair, (bigrams.get(pair) ?? 0) + 1);
      }
    }

    const updated: PersonalCounts = { unigrams, bigrams, lastUsed, display };
    set({ counts: updated, wordCount: unigrams.size });
    notify(updated);

    try {
      await personalNgramRepo.record(words, spokenAt);
    } catch (err) {
      // The in-memory counts are already correct for this session; only persistence failed. Losing a
      // sentence's worth of counts is a small, recoverable loss.
      console.error('[learning] could not persist counts', err);
    }
  },
}));

/** Listeners that want the counts pushed to them — the prediction client, in practice. */
type CountsListener = (counts: PersonalCounts) => void;
const listeners = new Set<CountsListener>();

export function onPersonalCounts(listener: CountsListener): () => void {
  listeners.add(listener);
  // Immediately, so a listener registering after hydration is not left with nothing until the next
  // sentence.
  listener(useLearning.getState().counts);
  return () => listeners.delete(listener);
}

function notify(counts: PersonalCounts): void {
  for (const listener of listeners) {
    try {
      listener(counts);
    } catch (err) {
      console.error('[learning] counts listener failed', err);
    }
  }
}

let unsubscribe: (() => void) | null = null;

export function startLearning(): () => void {
  if (unsubscribe) return unsubscribe;

  void useLearning.getState().hydrate();

  unsubscribe = subscribe((event: AppEvent) => {
    if (event.type !== 'UtteranceSpoken') return;
    // Fire-and-forget: nothing may sit between the person and being heard.
    void useLearning.getState().learn(event.text, event.source);
  });

  return unsubscribe;
}

export function stopLearning(): void {
  unsubscribe?.();
  unsubscribe = null;
}
